// Templates 04–09 · Invitee · teammate
// Highest-leverage emails in the system.

import {
  renderShell,
  renderStatusDot,
  renderHeading,
  renderParagraph,
  renderInfoCard,
  renderButtonRow,
  renderRsvpCluster,
  renderFooter,
  tokens,
  cleanSubject,
} from './kit.ts';
import { formatLongDate, formatTimeRange, formatShortDate, escapeHtml } from './format.ts';
import type {
  RenderedEmail,
  InviteData,
  RsvpConfirmationData,
  EventCancelledInviteeData,
  DayOfReminderData,
  RsvpNudge24hData,
  RsvpNudge12pmData,
} from './types.ts';

const fontStack = `'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
const monoStack = `ui-monospace, 'SF Mono', 'Roboto Mono', Menlo, monospace`;

// ─── 04 · INVITE WITH RSVP BUTTONS ────────────────────────────────────────
export function renderInvite(d: InviteData): RenderedEmail {
  const dateLong = formatLongDate(d.date);
  const dayShort = dateLong.split(',')[0];
  const subject = cleanSubject(
    `${d.organizerFirstName} invited you: drinks at ${d.vendorName}, ${dayShort} ${formatTimeRange(d.startTime, d.endTime).split(' – ')[0]}`,
  );
  const preheader = `Tap one button. The tab's pre-paid.`;

  const headerLabel = d.organizerOrgName
    ? `From ${d.organizerFirstName} · ${d.organizerOrgName}`
    : `From ${d.organizerFirstName}`;

  const body = `
    ${renderStatusDot("You're invited", 'action')}
    ${renderHeading(`Drinks at ${d.vendorName},`, 'on the company.')}
    ${renderParagraph(
      `${escapeHtml(d.organizerFirstName)} booked a round for the ${escapeHtml(d.organizerOrgName ?? 'team')}. The tab is already paid for — you just need to show up. Two-tap RSVP below.`,
    )}
    ${renderInfoCard(
      [
        { label: 'When', value: `${escapeHtml(dateLong)} · ${escapeHtml(formatTimeRange(d.startTime, d.endTime))}` },
        { label: 'Where', value: `${escapeHtml(d.vendorName)}${d.vendorAddress ? ` · ${escapeHtml(d.vendorAddress)}` : ''}` },
        { label: 'Package', value: `${escapeHtml(d.offerTitle)}${d.organizerOrgName ? `, on ${escapeHtml(d.organizerOrgName)}` : ''}` },
      ],
      { tone: 'amber', image: d.heroImage },
    )}
    ${renderRsvpCluster({ yesUrl: d.yesUrl, maybeUrl: d.maybeUrl, noUrl: d.noUrl })}
    ${renderParagraph(`${d.yesCount} of ${d.invitedCount} teammates have said yes so far.`, { muted: true })}
  `;

  const footerTagline = d.notOnTeamUrl
    ? `Not on this team? <a href="${escapeHtml(d.notOnTeamUrl)}" style="color:${tokens.inkSoft};text-decoration:underline;">Let us know</a>`
    : `One email per invite. Promise.`;

  // renderFooter escapes the tagline, so we inline HTML by post-processing:
  const footerHtml = renderFooter({ tagline: '__TAGLINE__', pill: 'Invite' }).replace('__TAGLINE__', footerTagline);

  return {
    subject,
    preheader,
    html: renderShell({ preheader, headerLabel, body, footer: footerHtml }),
  };
}

// ─── 05 · RSVP CONFIRMATION ───────────────────────────────────────────────
export function renderRsvpConfirmation(d: RsvpConfirmationData): RenderedEmail {
  const subject = cleanSubject(`You're in for ${d.vendorName} 🍸`);
  const preheader = `Calendar invite attached. We'll ping you 3 hours before.`;

  const body = `
    ${renderStatusDot("You're on the list", 'confirmed')}
    ${renderHeading('Cheers,', d.inviteeFirstName + '.')}
    ${renderParagraph(
      `You're confirmed for ${escapeHtml(formatShortDate(d.date))} at ${escapeHtml(d.vendorName)}. Calendar invite is attached. We'll send a reminder a few hours before with directions.`,
    )}
    ${renderInfoCard(
      [
        { label: 'When', value: `${escapeHtml(formatShortDate(d.date))} · ${escapeHtml(formatTimeRange(d.startTime, d.endTime))}` },
        { label: 'Where', value: `${escapeHtml(d.vendorName)}${d.vendorAddress ? ` · ${escapeHtml(d.vendorAddress)}` : ''}` },
        { label: 'Door', value: `"${escapeHtml(d.organizerOrgName ?? d.organizerFirstName)}" — they have the list` },
      ],
      { tone: 'amber' },
    )}
    ${renderButtonRow([
      { label: 'Add to calendar', href: d.calendarUrl, style: 'primary' },
      { label: 'Change RSVP', href: d.changeRsvpUrl, style: 'ghost' },
    ])}
  `;

  const footer = renderFooter({ tagline: `One email per invite. Promise.`, pill: 'Confirmed' });

  return {
    subject,
    preheader,
    html: renderShell({ preheader, headerLabel: 'RSVP confirmed', body, footer }),
  };
}

// ─── 06 · EVENT CANCELLED (INVITEE) ───────────────────────────────────────
export function renderEventCancelledInvitee(d: EventCancelledInviteeData): RenderedEmail {
  const dayShort = formatLongDate(d.date).split(',')[0];
  const subject = cleanSubject(`Heads up: ${d.vendorName} ${dayShort.toLowerCase()} is cancelled`);
  const preheader = `Don't head over. ${d.organizerFirstName} will reschedule.`;

  const body = `
    ${renderStatusDot('Cancelled', 'cancelled')}
    ${renderHeading(`Don't head to ${d.vendorName}`, dayShort.toLowerCase() + '.')}
    ${renderParagraph(
      `${escapeHtml(d.organizerFirstName)} cancelled the round (or it was auto-cancelled due to low RSVPs). Either way, the bar isn't expecting you — so save the trip.`,
    )}
    ${renderInfoCard(
      [
        { label: 'Was', value: `${escapeHtml(dayShort)} · ${escapeHtml(formatTimeRange(d.startTime, d.endTime))} · ${escapeHtml(d.vendorName)}` },
        { label: 'Status', value: `Cancelled at ${escapeHtml(d.cancelledAt)}` },
      ],
      { tone: 'pink' },
    )}
    ${renderParagraph(`Your calendar event has been removed automatically. Sorry for the late notice.`, { muted: true })}
  `;

  const footer = renderFooter({ tagline: `Triggered by your organizer`, pill: 'Cancelled' });

  return {
    subject,
    preheader,
    html: renderShell({ preheader, headerLabel: 'Important', body, footer }),
  };
}

// ─── 07 · DAY-OF REMINDER T-3h ────────────────────────────────────────────
export function renderDayOfReminder(d: DayOfReminderData): RenderedEmail {
  const subject = cleanSubject(`${d.vendorName} in 3 hours · ${d.yesCount} teammates coming`);
  const preheader = `${d.vendorAddress ?? ''} · just say "${d.organizerOrgName ?? d.organizerFirstName}" at the door.`;

  // Subway-line color bubbles for attendee initials.
  const subwayColors = ['#ee352e', '#fccc0a', '#0039a6', '#00933c', '#a626aa', '#ff6319'];
  const initials = (d.attendeeInitials ?? []).slice(0, 5).map((letter, i) => {
    const bg = subwayColors[i % subwayColors.length];
    return `<span style="display:inline-block;width:26px;height:26px;line-height:26px;border-radius:50%;background:${bg};color:#fff;font-family:${fontStack};font-weight:700;font-size:13px;text-align:center;margin-right:-6px;border:2px solid ${tokens.cardAmber};vertical-align:middle;">${escapeHtml(letter)}</span>`;
  }).join('');

  const attendeeRow = `<div style="background:${tokens.cardAmber};border-radius:999px;padding:10px 18px;margin:0 0 24px;font-family:${fontStack};font-size:14px;color:${tokens.ink};">
    ${initials}
    <span style="margin-left:14px;vertical-align:middle;">${d.yesCount} teammates yes · ${d.maybeCount} maybe${d.maybeCount === 1 ? '' : 's'} still drifting in</span>
  </div>`;

  const body = `
    ${renderStatusDot('Tonight', 'action')}
    ${renderHeading(`Cocktails at ${d.vendorName.split(' ')[0]} in`, 'three.')}
    ${renderParagraph(
      `Subway-level details, in case you needed them: <strong>${escapeHtml(d.vendorAddress ?? d.vendorName)}</strong>. Just say "${escapeHtml(d.organizerOrgName ?? d.organizerFirstName)}" at the door.`,
    )}
    ${attendeeRow}
    ${renderButtonRow([
      { label: 'Get directions', href: d.directionsUrl, style: 'primary' },
      { label: "Can't make it after all", href: d.changeRsvpUrl, style: 'ghost' },
    ])}
  `;

  const footer = renderFooter({ tagline: `Sent because you RSVP'd yes`, pill: 'Reminder' });

  return {
    subject,
    preheader,
    html: renderShell({ preheader, headerLabel: '3 hours out', body, footer }),
  };
}

// ─── 08 · RSVP NUDGE 24H PRIOR ────────────────────────────────────────────
export function renderRsvpNudge24h(d: RsvpNudge24hData): RenderedEmail {
  const subject = cleanSubject(`Still in for ${d.vendorName} tomorrow?`);
  const preheader = `One tap and we'll stop bothering you.`;

  const needed = Math.max(0, (d.floor ?? 4) - d.yesCount);

  const body = `
    ${renderStatusDot('Tomorrow at ' + formatTimeRange(d.startTime, d.endTime).split(' ')[0], 'heads-up')}
    ${renderHeading('Still rolling with us', 'tomorrow?')}
    ${renderParagraph(
      `No pressure — but ${escapeHtml(d.vendorName)} needs a final headcount, and you're still on the maybe pile. One tap, we lock it in, and we won't ping you again.`,
    )}
    ${renderRsvpCluster({ yesUrl: d.yesUrl, maybeUrl: d.maybeUrl, noUrl: d.noUrl, maybeLabel: 'Still a maybe' })}
    ${renderParagraph(
      `${d.yesCount} of ${d.invitedCount} teammates yes so far.${needed > 0 ? ` We need ${needed} to keep it on the books.` : ''}`,
      { muted: true },
    )}
  `;

  const footer = renderFooter({ tagline: `Nudge 1 of 2`, pill: 'Nudge' });

  return {
    subject,
    preheader,
    html: renderShell({ preheader, headerLabel: 'Quick check-in', body, footer }),
  };
}

// ─── 09 · RSVP NUDGE 12 PM DAY-OF ─────────────────────────────────────────
export function renderRsvpNudge12pm(d: RsvpNudge12pmData): RenderedEmail {
  const startTime = formatTimeRange(d.startTime, d.endTime).split(' – ')[0];
  const subject = cleanSubject(`Last call: ${d.vendorName} at ${startTime} today`);
  const preheader = `We charge ${d.organizerFirstName}'s card at 3. Yes or no?`;

  const body = `
    ${renderStatusDot('Three hours', 'heads-up')}
    ${renderHeading('Yes or', 'no', `on ${d.vendorName} tonight?`)}
    ${renderParagraph(
      `We charge ${escapeHtml(d.organizerFirstName)}'s card at 3 PM sharp and lock the headcount with the bar. After that we can't add you. A "no" right now is also fine — we'd rather know.`,
    )}
    ${renderRsvpCluster({
      yesUrl: d.yesUrl,
      maybeUrl: d.maybeUrl,
      noUrl: d.noUrl,
      yesLabel: 'Yes, count me in',
      noLabel: "Can't make it",
      maybeLabel: 'Maybe',
    })}
    <div style="background:${tokens.cardAmber};border-left:3px solid ${tokens.amber};border-radius:6px;padding:14px 18px;margin:0 0 24px;font-family:${fontStack};font-size:14px;line-height:1.5;color:${tokens.ink};">
      <strong>Quick math:</strong> a yes from you means $${Math.round(d.pricePerHeadCents / 100)} stays on the team card. A late no means it doesn't.
    </div>
  `;

  const footer = renderFooter({ tagline: `Final nudge · 2 of 2`, pill: 'Last call' });

  return {
    subject,
    preheader,
    html: renderShell({ preheader, headerLabel: `Last call · 3 hours to lock`, body, footer }),
  };
}
